import { Button } from '../ui/button';
import { toast } from 'sonner';
import { FileText, Download, Presentation } from 'lucide-react';

export const MarketingSection = () => (
  <div className="bg-white border border-stone-200 rounded-2xl p-6 mb-8" data-testid="marketing-materials-section">
    <div className="flex items-center gap-2 mb-4">
      <FileText className="w-5 h-5 text-amber-600" />
      <h3 className="text-lg font-semibold text-stone-900">Marketing Materials</h3>
    </div>
    <p className="text-sm text-stone-500 mb-5">Download presentation decks and marketing video for business meetings and promotions.</p>
    <div className="grid sm:grid-cols-3 gap-4">
      <DeckCard
        title="Concise Overview"
        subtitle="8 slides — Key features"
        icon={<FileText className="w-6 h-6 text-amber-600" />}
        iconBg="bg-amber-50"
        btnClass="bg-amber-600 text-white hover:bg-amber-700"
        deckType="concise"
        filename="TripleA_Travel_Solutions_Overview.pptx"
        testId="download-concise-deck"
      />
      <DeckCard
        title="Detailed Deck"
        subtitle="16 slides — Full overview"
        icon={<FileText className="w-6 h-6 text-teal-700" />}
        iconBg="bg-teal-50"
        btnClass="bg-teal-700 text-white hover:bg-teal-800"
        deckType="detailed"
        filename="TripleA_Travel_Solutions_Detailed.pptx"
        testId="download-detailed-deck"
      />
      <VideoCard />
    </div>
  </div>
);

const DeckCard = ({ title, subtitle, icon, iconBg, btnClass, deckType, filename, testId }) => (
  <div className="border border-stone-200 rounded-xl p-5 flex flex-col items-center gap-3 hover:shadow-md transition-shadow text-center">
    <div className={`w-12 h-12 rounded-xl ${iconBg} flex items-center justify-center flex-shrink-0`}>
      {icon}
    </div>
    <div>
      <p className="text-sm font-semibold text-stone-800">{title}</p>
      <p className="text-xs text-stone-500">{subtitle}</p>
    </div>
    <Button
      onClick={() => {
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/presentation/${deckType}`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } })
          .then(r => r.blob())
          .then(blob => { const url = URL.createObjectURL(blob); const link = document.createElement('a'); link.href = url; link.download = filename; link.click(); URL.revokeObjectURL(url); toast.success(`${title} downloaded`); })
          .catch(() => toast.error('Download failed'));
      }}
      size="sm"
      className={`${btnClass} rounded-full px-4 w-full`}
      data-testid={testId}
    >
      <Download className="w-4 h-4 mr-1" /> PPTX
    </Button>
  </div>
);

const VideoCard = () => (
  <div className="border border-stone-200 rounded-xl p-5 flex flex-col items-center gap-3 hover:shadow-md transition-shadow text-center">
    <div className="w-12 h-12 rounded-xl bg-rose-50 flex items-center justify-center flex-shrink-0">
      <Presentation className="w-6 h-6 text-rose-600" />
    </div>
    <div>
      <p className="text-sm font-semibold text-stone-800">Marketing Video</p>
      <p className="text-xs text-stone-500">MP4 — Share with users</p>
    </div>
    <Button
      onClick={async () => {
        const base = process.env.REACT_APP_BACKEND_URL;
        toast.info('Generating video with voiceover... this may take 1-2 minutes.');
        try {
          await fetch(`${base}/api/admin/marketing-video/generate`, { method: 'POST', headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
          const poll = setInterval(async () => {
            try {
              const res = await fetch(`${base}/api/admin/marketing-video/status`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
              const data = await res.json();
              if (data.status === 'ready') {
                clearInterval(poll);
                const r = await fetch(`${base}/api/admin/marketing-video`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
                const blob = await r.blob();
                const url = URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = 'TripleA_Travel_Solutions_Marketing.mp4';
                link.click();
                URL.revokeObjectURL(url);
                toast.success('Marketing video downloaded!');
              } else if (data.status === 'error') {
                clearInterval(poll);
                toast.error('Video generation failed: ' + data.message);
              }
            } catch { clearInterval(poll); toast.error('Status check failed'); }
          }, 5000);
        } catch { toast.error('Failed to start video generation'); }
      }}
      size="sm"
      className="bg-rose-600 text-white hover:bg-rose-700 rounded-full px-4 w-full"
      data-testid="download-marketing-video"
    >
      <Download className="w-4 h-4 mr-1" /> MP4
    </Button>
  </div>
);
